import { useRef, useState } from "react";
import {
  AGENT_MEDIA_ACCEPT,
  AGENT_MEDIA_LIMITS,
  type DocumentEntry,
} from "@school-sales-ai/shared";
import {
  useDeactivateDocumentMutation,
  useDocumentsQuery,
  useUpdateDocumentMutation,
  useUploadDocumentMutation,
} from "@/hooks/useDocuments";
import { DeleteDocumentDialog } from "@/components/DeleteDocumentDialog";
import { EditDocumentModal } from "@/components/EditDocumentModal";
import {
  AGENT_MEDIA_LIMITS_HINT,
  checkAgentMediaFile,
  formatFileSize,
} from "@/lib/agent-media-file";
import { getDocumentDownloadUrlApi } from "@/lib/api";
import { toast } from "sonner";

const TITLE_MAX = 120;
const DESCRIPTION_MAX = 500;

export function DocumentsManager(): React.JSX.Element {
  const { data: documents, isLoading, isError } = useDocumentsQuery();
  const uploadMutation = useUploadDocumentMutation();
  const updateMutation = useUpdateDocumentMutation();
  const deactivateMutation = useDeactivateDocumentMutation();

  // Upload form state
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Metadata-edit modal state
  const [editingDoc, setEditingDoc] = useState<DocumentEntry | null>(null);
  const [deletingDoc, setDeletingDoc] = useState<DocumentEntry | null>(null);

  // Same catalogue the server validates against, so the cap an admin reads here
  // is the one that will be enforced. The video CODEC can only be checked
  // server-side; its message arrives through the upload's error toast.
  const { error: fileError } = checkAgentMediaFile(file);
  const canSubmit =
    file !== null &&
    fileError === null &&
    title.trim().length > 0 &&
    title.length <= TITLE_MAX &&
    description.trim().length > 0 &&
    description.length <= DESCRIPTION_MAX &&
    !uploadMutation.isPending;

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setFile(e.target.files?.[0] ?? null);
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!file || !canSubmit) return;
    uploadMutation.mutate(
      {
        file,
        title: title.trim(),
        description: description.trim(),
      },
      {
        onSuccess: () => {
          setFile(null);
          setTitle("");
          setDescription("");
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
      },
    );
  }

  async function handlePreview(doc: DocumentEntry): Promise<void> {
    try {
      const url = await getDocumentDownloadUrlApi(doc.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al abrir el documento.");
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6">
      <div className="max-w-3xl flex flex-col gap-6">
        {/* Upload form */}
        <form
          onSubmit={handleSubmit}
          className="rounded-[14px] border border-ink-100 bg-white p-4 md:p-5 flex flex-col gap-3"
        >
          <h2 className="text-sm font-semibold text-ink-800">Subir material</h2>
          <p className="text-xs text-ink-500">
            Materiales generales (PDF, imagen o video) que no pertenecen a un programa: un
            reglamento, una lista de precios, el calendario escolar. El agente los envía por
            WhatsApp cuando un prospecto los pide; las imágenes y los videos llegan como foto y
            video, no como archivo adjunto.
          </p>
          <p className="text-xs text-ink-500">
            Las fotos, el video y el PDF de cada programa se suben en{" "}
            <span className="font-medium text-ink-700">Catálogo → ⋯ → Medios</span>.
          </p>

          <div className="flex flex-col gap-1">
            <label htmlFor="doc-file" className="text-xs font-medium text-ink-600">
              Archivo ({AGENT_MEDIA_LIMITS_HINT})
            </label>
            <input
              id="doc-file"
              ref={fileInputRef}
              type="file"
              accept={AGENT_MEDIA_ACCEPT}
              onChange={handleFileChange}
              className="text-sm text-ink-700 file:mr-3 file:rounded-lg file:border-0 file:bg-ink-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-ink-700 hover:file:bg-ink-200"
            />
            {fileError && <p className="text-xs text-red-500">{fileError}</p>}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="doc-title" className="text-xs font-medium text-ink-600">
              Título
            </label>
            <input
              id="doc-title"
              type="text"
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Reglamento escolar"
              className="rounded-[10px] border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="doc-description" className="text-xs font-medium text-ink-600">
              Descripción (el agente la usa para decidir cuándo compartirlo)
            </label>
            <textarea
              id="doc-description"
              value={description}
              maxLength={DESCRIPTION_MAX}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Reglas de convivencia, uniformes y horarios de atención…"
              className="resize-none rounded-[10px] border border-ink-200 px-3 py-2 text-sm text-ink-800 focus:border-moss focus:outline-none"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-lg bg-lime px-4 py-2 text-sm font-medium text-moss hover:bg-lime-dk disabled:cursor-not-allowed disabled:opacity-40"
            >
              {uploadMutation.isPending ? "Subiendo…" : "Subir material"}
            </button>
          </div>
        </form>

        {/* Document list */}
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-ink-800">Documentos generales</h2>
          {isLoading && (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-[14px] bg-ink-100" />
              ))}
            </div>
          )}
          {isError && (
            <p className="text-sm text-red-500">Error al cargar los documentos.</p>
          )}
          {documents && documents.length === 0 && (
            <p className="text-sm text-ink-400">
              Aún no hay documentos. Sube el primero con el formulario de arriba.
            </p>
          )}
          {documents?.map((doc) => (
            <div
              key={doc.id}
              className={`rounded-[14px] border border-ink-100 bg-white p-4 flex flex-col gap-2 ${
                doc.isActive ? "" : "opacity-60"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink-900 truncate">
                    {AGENT_MEDIA_LIMITS[doc.kind].icon} {doc.title}
                  </p>
                  <p className="text-xs text-ink-500 mt-0.5">{doc.description}</p>
                  <p className="text-[11px] text-ink-400 mt-1">
                    {AGENT_MEDIA_LIMITS[doc.kind].label} · {doc.fileName} ·{" "}
                    {formatFileSize(doc.fileSizeBytes)}
                    {/* Everything here is general now; only a document linked before
                        product media existed (and not moved, e.g. inactive) still
                        names its product. */}
                    {doc.productName ? ` · ${doc.productName}` : ""}
                    {!doc.isActive && " · Inactivo"}
                  </p>
                </div>
                <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => void handlePreview(doc)}
                    className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-050"
                  >
                    Ver
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingDoc(doc)}
                    className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-050"
                  >
                    Editar
                  </button>
                  {doc.isActive ? (
                    <button
                      type="button"
                      disabled={deactivateMutation.isPending}
                      onClick={() => deactivateMutation.mutate(doc.id)}
                      className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-050 disabled:opacity-40"
                    >
                      Desactivar
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={updateMutation.isPending}
                      onClick={() =>
                        updateMutation.mutate({ id: doc.id, data: { isActive: true } })
                      }
                      className="rounded-lg border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-050 disabled:opacity-40"
                    >
                      Reactivar
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setDeletingDoc(doc)}
                    className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <EditDocumentModal doc={editingDoc} onClose={() => setEditingDoc(null)} />
      <DeleteDocumentDialog doc={deletingDoc} onClose={() => setDeletingDoc(null)} />
    </div>
  );
}
